import * as React from "react";
import {useCallback, useEffect, useMemo, useRef, useState} from "react";
import {FormattedMessage} from "react-intl";
import {UserApi} from "../../api/UserApi";
import {IUserInfo, UserInfo, UserTypeEnum} from "../../model/UserInfo.model";
import {LoadingSpinner} from "../icons/LoadingSpinner.component";
import {ErrorComponent} from "../base/Error.component";
import {getTranslation} from "../../utils/translationUtil";
import {UserSearchDropdown} from "./UserSearchDropdown.component";
import Toast, {ToastType} from "../common/Toast.component";
import {extractApiErrorMessage, IToastNotification, removePlanFromUsers, TOAST_NOTIFICATION_TIMEOUT_MS} from "../../utils/planUserRemoval";

export interface IPlanUsersTabProps {
    planId: number;
    planName: string;
}

export const PlanUsersTab: React.FunctionComponent<IPlanUsersTabProps> = (props) => {
    const [allUsers, setAllUsers] = useState<IUserInfo[]>([]);
    const [loading, setLoading] = useState<boolean>(true);
    const [error, setError] = useState<boolean>(false);
    const [toast, setToast] = useState<IToastNotification | null>(null);
    const toastTimeoutRef = useRef<number | null>(null);

    useEffect(() => {
        setLoading(true);
        UserApi.requestUsers().then((response) => {
            response.data ? setAllUsers(response.data): setError(true);
        })
        .catch(() => setError(true))
        .finally(() => setLoading(false));
    }, []);

    useEffect(() => {
        return () => {
            if (toastTimeoutRef.current !== null) {
                window.clearTimeout(toastTimeoutRef.current);
            }
        };
    }, []);

    const assignedUsers = useMemo(() => {
        return allUsers
            .filter((u) => u.planIds.includes(props.planId))
            .sort((a, b) => a.email.localeCompare(b.email));
    }, [allUsers, props.planId]);

    const formatRole = (userType: UserTypeEnum): string => {
        return userType.charAt(0) + userType.slice(1).toLowerCase();
    };

    const showToast = useCallback((message: string, type: ToastType = "success") => {
        if (toastTimeoutRef.current !== null) {
            window.clearTimeout(toastTimeoutRef.current);
        }
        setToast({message, type});
        toastTimeoutRef.current = window.setTimeout(() => {
            setToast(null);
            toastTimeoutRef.current = null;
        }, TOAST_NOTIFICATION_TIMEOUT_MS);
    }, []);

    const handleAssignUser = useCallback((user: IUserInfo) => {
        const updatedPlanIds = [...user.planIds, props.planId];
        UserApi.updateUser(user.arbtSecurityId, user.userType, updatedPlanIds).then(() => {
                setAllUsers((prev) => prev.map((u) =>
                    u.arbtSecurityId === user.arbtSecurityId
                    ? new UserInfo(u.firstName, u.lastName, u.email, u.lastLoginDate,
                        u.userType, u.arbtSecurityId, updatedPlanIds)
                    : u,
                ));
                showToast(`${user.firstName} ${user.lastName} ${getTranslation("admin.plan-users.added", "added to plan")}`);
            }
        ).catch(() => {
            showToast(
                `${getTranslation("admin.plan-users.error-assign", "Failed to assign")} ${user.firstName} ${user.lastName}`,
                "error",
            );
        });
    }, [props.planId, showToast]);

    const handleRemoveUser = useCallback((user: IUserInfo) => {
        UserApi.removeUserFromPlan(user.arbtSecurityId, props.planId).then(() => {
            setAllUsers((prev) => removePlanFromUsers(prev, user.arbtSecurityId, props.planId));
            showToast(`${user.firstName} ${user.lastName} ${getTranslation("admin.plan-users.removed", "removed from plan")}`);
        }).catch((removeError: unknown) => {
            const errorMessage = extractApiErrorMessage(removeError);
            showToast(
                errorMessage.includes("ATC")
                    ? "ATC admin users can only update the Aon Collective Investment Trust plan."
                    : `Failed to remove ${user.firstName} ${user.lastName}`,
                "error"
            );
        });
    }, [props.planId, showToast]);

    if (loading) return <LoadingSpinner />;
    if (error) return <ErrorComponent />;

    const renderSearchDropdown = () => {
      return <div className="plan-users-tab__header">
          <UserSearchDropdown
              allUsers={allUsers}
              planId={props.planId}
              planName={props.planName}
              onAssignUser={handleAssignUser}
          />
          <div className="plan-users-tab__count">
            <span className="plan-users-tab__count-badge" data-testid="plan-users-tab__count">
                {assignedUsers.length}{" "}
                {assignedUsers.length === 1
                    ? getTranslation("admin.plan-users.user-assigned", "user assigned")
                    : getTranslation("admin.plan-users.users-assigned", "users assigned")}
            </span>
          </div>
      </div>;
    };

    const renderEmptyUserMessage = () => {
        return <div className="plan-users-tab__empty" data-testid="plan-users-tab__empty">
            <div className="plan-users-tab__empty-icon">
                <i className="fa-solid fa-users" />
            </div>
            <h3><FormattedMessage id="admin.plan-users.no-users" defaultMessage="No users assigned" /></h3>
            <p><FormattedMessage id="admin.plan-users.no-users-hint" defaultMessage="Search for users above to add them to this plan." /></p>
        </div>;
    };

    const renderUserTable = () => {
        return <div className="plan-users-tab__table-wrapper">
            <table className="plan-users-tab__table"
                   aria-label={getTranslation("admin.plan-users.table-aria", "Users assigned to this plan")}>
                <thead>
                <tr>
                    <th scope="col"><FormattedMessage id="admin.plan-users.th.first-name" defaultMessage="First Name" /></th>
                    <th scope="col"><FormattedMessage id="admin.plan-users.th.last-name" defaultMessage="Last Name" /></th>
                    <th scope="col"><FormattedMessage id="admin.plan-users.th.email" defaultMessage="Email" /></th>
                    <th scope="col"><FormattedMessage id="admin.plan-users.th.role" defaultMessage="Role" /></th>
                    <th scope="col"><FormattedMessage id="admin.plan-users.th.last-login" defaultMessage="Last Login" /></th>
                    <th scope="col"><FormattedMessage id="asset-class.Equity" defaultMessage="Actions" /></th>
                </tr>
                </thead>
                <tbody>
                {assignedUsers.map((user) => (
                    <tr key={user.arbtSecurityId} data-testid={`user-row-${user.arbtSecurityId}`}>
                        <td>{user.firstName}</td>
                        <td>{user.lastName}</td>
                        <td className="plan-users-tab__email">{user.email}</td>
                        <td className="plan-users-tab__role">{formatRole(user.userType)}</td>
                        <td className="plan-users-tab__last-login">
                            {user.lastLoginDate || getTranslation("admin.plan-users.never", "Never")}
                        </td>
                        <td className="plan-users-tab__actions">
                            <button type="button"
                                 className="plan-users-tab__remove-btn"
                                 onClick={() => handleRemoveUser(user)}
                                 aria-label={`${getTranslation("admin.plan-users.remove-aria", "Remove")} ${user.firstName} ${user.lastName} ${getTranslation("admin.plan-users.from-plan", "from plan")}`}
                                 data-testid={`plan-users__remove-${user.arbtSecurityId}`}>
                                <i className="fas fa-trash" aria-hidden="true" />
                            </button>
                        </td>
                    </tr>
                ))}
                </tbody>
            </table>
        </div>;
    };

    return (
        <div className="plan-users-tab__container" data-testid="plan-users-tab__container">
            {renderSearchDropdown()}
            {assignedUsers.length === 0 ? renderEmptyUserMessage() : renderUserTable()}
            <Toast visible={!!toast} message={toast?.message || ""} type={toast?.type || "success"} />
        </div>
    );
};
